"use client";
import dynamic from "next/dynamic";
import Navigation from "@/HomeFeatures/Navigation";
import Image from "next/image";
import Link from "next/link";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons, Images } from "@assets/assets";
import TeamHRD from "../../career/sections/TeamHRD";
import Openings from "../../career/sections/Openings";
import { JobDetailsProps } from "@/lib/Types";
import { formatDate } from "@/lib/Utility";
import { useScrollToContact } from "@/lib/ScrollUtils";

const ContactSection = dynamic(() => import("@/HomeFeatures/Contact2"));
export default function CareerDetailPage({ initialBlog }: JobDetailsProps) {
  const handleSmoothScroll = useScrollToContact();

  const jobCreatedDate = formatDate(initialBlog?.modified);
  const contentSections = [
    {
      title: "Job Description:",
      content:
        initialBlog?.description ||
        "We are on for UI Designers who know how to translate their passion for creativity into lovely web and mobile interfaces. Those who can weave feelings, visualize ideas and craft out-of-the-box designs. We have clients from different parts of the world coming up with creative challenges and ceaseless opportunities requiring you to portray your artistic wits into impressive crafts.",
      pre: false,
    },
    {
      title: "Requirements and Duties:",
      content:
        initialBlog?.requirements_duties ||
        "Participate in direct meetings and teleconferences with clients\nWork in an Agile team environment\nClosely work with the Product team to ensure the best UX solutions are being developed\nActively involve with business development process via creating pitch decks, networking, or supplying direct leads\nCollaborate with the development team to deliver projects on time",
      pre: true,
    },
    {
      title: "Skill and Ability:",
      content:
        initialBlog?.skills_ability ||
        "Knowledge with RWD, UX, Branding and Iconography\nProficient in layout designing and converting them to XTHML/HTML/CSS\nShould be able to work on programs like Fireworks, Photoshop, Illustrator, Sketch, Dreamweaver etc.\nDeveloping web and mobile designs using HTML or GUI design software\nAbility to create, ideate and generate different UX resources to be used with different projects",
      pre: true,
    },
  ];

  const rightRailItems = [
    {
      icon: Images?.jobDetails.translate,
      label: "Language",
      value: initialBlog?.language || "English: Advance/Fluent",
      alt: "Language",
    },
    {
      icon: Images?.jobDetails.location,
      label: "Location",
      value: initialBlog?.location || "Jaipur, Rajasthan",
      alt: "Location",
    },
    {
      icon: Images?.jobDetails.department,
      label: "Department",
      value: initialBlog?.department || "Frontend Developer",
      alt: "Department",
    },
    {
      icon: Images?.jobDetails.time,
      label: "Job Type",
      value:
        initialBlog?.job_type === 1
          ? "Full time"
          : initialBlog?.job_type === 2
            ? "Part Time"
            : "Full time",
      alt: "Job Type",
    },
    {
      icon: Images?.jobDetails.experience,
      label: "Experience",
      value: initialBlog?.experience || "4+ years",
      alt: "Experience",
    },
    {
      icon: Images?.jobDetails.time,
      label: "Job Posted",
      value: jobCreatedDate || "30 Aug, 2025",
      alt: "Job Posted",
    },
  ];

  return (
    <>
      <section className="relative h-auto flex flex-col max-w-full">
        {/* here */}
        <div className="relative z-10 w-full">
          <div className="w-full h-full min-h-[100vh] bg-[#fff] flex flex-col pb-[100px]">
            <Navigation dark={false} />
            {/* Breadcrumb */}
            <div className="w-[calc(100%_-_10%)] max-w-[2500px] mx-auto mt-4">
              <nav
                aria-label="Breadcrumb"
                className="h-[24px] py-3 px-3 flex items-center gap-2 text-[14px] text-black/60 border shadow-[0_2px_12px_0_rgba(0,0,0,0.15)] rounded-[99px] border-solid border-white w-[max-content] max-w-[100%]"
              >
                <Link
                  href="/"
                  aria-label="Go to homepage"
                  className="hover:text-black"
                >
                  Home
                </Link>
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center rotate-180"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.1653 4.23431C10.4777 4.54673 10.4777 5.05327 10.1653 5.36569L7.53098 8L10.1653 10.6343C10.4777 10.9467 10.4777 11.4533 10.1653 11.7657C9.85288 12.0781 9.34634 12.0781 9.03392 11.7657L5.83392 8.56569C5.5215 8.25327 5.5215 7.74673 5.83392 7.43431L9.03392 4.23431C9.34634 3.9219 9.85288 3.9219 10.1653 4.23431Z"
                      fill="#00040B"
                    />
                  </svg>
                </span>
                <Link
                  href="/career"
                  aria-label="Go to career page"
                  className="hover:text-black"
                >
                  Career
                </Link>
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center rotate-180"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.1653 4.23431C10.4777 4.54673 10.4777 5.05327 10.1653 5.36569L7.53098 8L10.1653 10.6343C10.4777 10.9467 10.4777 11.4533 10.1653 11.7657C9.85288 12.0781 9.34634 12.0781 9.03392 11.7657L5.83392 8.56569C5.5215 8.25327 5.5215 7.74673 5.83392 7.43431L9.03392 4.23431C9.34634 3.9219 9.85288 3.9219 10.1653 4.23431Z"
                      fill="#00040B"
                    />
                  </svg>
                </span>
                <span className="text-black">
                  {initialBlog?.job_title || "Frontend Developer"}
                </span>
              </nav>
            </div>
            <div className="w-[calc(100%_-_10%)] max-w-[2500px] mx-auto mt-6 md:mt-10">
              <div className="flex flex-col xl:flex-row gap-[30px]">
                {/* Left column */}
                <div className="flex-1 border border-[#ecf0f6] rounded-[12px]">
                  <div className="p-[30px]">
                    <h1 className="self-stretch justify-start text-[length:var(--section-title)] font-medium clash-font text-black min-w-[14ch] leading-[100%] tracking-[-0.02em] pb-3">
                      We’re looking for a{" "}
                      <span style={{ color: "#225bd4" }}>
                        {initialBlog?.job_title}
                      </span>
                    </h1>
                    <div className="mt-4 flex flex-wrap gap-3 xl:gap-1 xl:bg-[#ECF0F6] rounded-full max-w-full w-[max-content]">
                      {initialBlog?.technologies?.map((items, idx) => (
                        <span
                          key={idx}
                          className={`xl:px-[30px] bg-[#ECF0F6] xl:bg-transparent 
                                                px-4 rounded-[30px] py-[10px] text-[15px] text-black/80 
                                                ${idx < initialBlog?.technologies.length - 1 ? "border-r border-[#ffffff]" : ""}`}
                        >
                          {items?.name}
                        </span>
                      ))}
                    </div>

                    <div className="mt-[30px] flex flex-col gap-[30px]">
                      {contentSections?.map(({ title, content, pre }) => (
                        <div key={title} className="flex flex-col gap-5">
                          <h2 className="text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[120%] font-medium clash-font text-black">
                            {title}
                          </h2>
                          <p
                            className={`${pre ? "whitespace-pre-line" : ""} text-[length:clamp(1rem,0.8384rem+0.3367vw,1.375rem)] leading-[140%] text-[#636364]`}
                          >
                            {content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className="w-full xl:w-[552px] flex-shrink-0">
                  <div className="w-full xl:w-[552px] flex-shrink-0 flex flex-col gap-[30px] sticky top-[15px]">
                    <div className="border border-[#ecf0f6] rounded-[12px]">
                      <div className="p-[30px]">
                        <div className="w-[120px] h-[120px] border border-[#ecf0f6] box-border p-6 rounded-[16px] grid place-items-center">
                          <Image
                            src={Images?.logoIcon}
                            className="w-full h-full object-contain"
                            alt="Job"
                            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                            priority
                            fetchPriority="high"
                            width={120}
                            height={120}
                          />
                        </div>
                        <h3 className="mt-5 text-[36px] leading-[120%] font-medium clash-font text-black">
                          Konsatnt Infosolutions
                          <br />
                          Pvt Ltd
                        </h3>
                        <p className="mt-2 text-[14px] leading-[164%] clash-font text-black">
                          Information Technology & Service
                        </p>
                        <PrimaryBtn
                          text="Apply for this role"
                          styleClass="mt-5"
                          iconSrc={Icons?.chevron}
                          dark={false}
                          onClick={() => {
                            handleSmoothScroll();
                          }}
                        />
                      </div>
                    </div>

                    <div className="border border-[#ecf0f6] rounded-[12px] p-[30px] flex flex-col gap-[30px]">
                      {rightRailItems?.map(({ icon, label, value, alt }) => (
                        <div key={label} className="flex items-center gap-4">
                          <div className="w-[54px] h-[54px] grid place-items-center border border-[#ecf0f6] rounded-[8px]">
                            <Image
                              src={icon}
                              alt={alt}
                              width={24}
                              height={24}
                              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                              priority
                              fetchPriority="high"
                            />
                          </div>
                          <div>
                            <p className="text-[14px] leading-[164%] text-[#636364]">
                              {label}
                            </p>
                            <p className="text-[length:clamp(1rem,0.8384rem+0.3367vw,1.375rem)] leading-[132%] font-medium">
                              {value}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Openings baseURL="/career" />
      <TeamHRD />
      <ContactSection variant="default" formType="career" />
    </>
  );
}
